// Address family of the WebRTC path (pure, no DOM; unit-tested in Node). See SPEC.md
// "Client library" (selectedPairInfo().family).

const V4_OCTET = '(?:25[0-5]|2[0-4]\\d|1\\d\\d|[1-9]?\\d)';
const V4 = new RegExp(`^${V4_OCTET}(?:\\.${V4_OCTET}){3}$`);
const HEX_GROUP = /^[0-9a-f]{1,4}$/i;

// Groups of an IPv6 part: hex groups, and (when allowed) a dotted IPv4 tail as two groups.
function groupsOf(parts, allowV4Tail) {
  const out = [];
  for (let i = 0; i < parts.length; i++) {
    const p = parts[i];
    if (allowV4Tail && i === parts.length - 1 && p.includes('.')) {
      if (!V4.test(p)) return null;
      const b = p.split('.').map(Number);
      out.push((b[0] << 8) | b[1], (b[2] << 8) | b[3]);
    } else if (HEX_GROUP.test(p)) {
      out.push(parseInt(p, 16));
    } else {
      return null;
    }
  }
  return out;
}

// IPv6 literal -> 8 numbers (0..65535), or null.
function parseIPv6(s) {
  if (!s.includes(':') || !/^[0-9a-f:.]+$/i.test(s)) return null;
  const gap = s.indexOf('::');
  if (gap === -1) {
    const g = groupsOf(s.split(':'), true);
    return g && g.length === 8 ? g : null;
  }
  if (s.indexOf('::', gap + 1) !== -1) return null;
  const left = s.slice(0, gap);
  const right = s.slice(gap + 2);
  const l = left ? groupsOf(left.split(':'), false) : [];
  const r = right ? groupsOf(right.split(':'), true) : [];
  if (!l || !r || l.length + r.length > 7) return null;
  return [...l, ...new Array(8 - l.length - r.length).fill(0), ...r];
}

// 'ipv4' | 'ipv6' | null for an IP literal ("[..]" and a "%zone" are accepted).
// IPv4-mapped IPv6 (::ffff:a.b.c.d) is 'ipv4': those packets travel as IPv4.
// Hostnames (e.g. mDNS "….local"), unspecified addresses and anything else give null.
export function addressFamily(address) {
  if (typeof address !== 'string') return null;
  let s = address.trim();
  if (s.startsWith('[') && s.endsWith(']')) s = s.slice(1, -1);
  const zone = s.indexOf('%');
  if (zone !== -1) s = s.slice(0, zone);
  if (V4.test(s)) return s === '0.0.0.0' ? null : 'ipv4';
  const g = parseIPv6(s);
  if (!g) return null;
  if (g.every((x) => x === 0)) return null;
  if (g.slice(0, 5).every((x) => x === 0) && g[5] === 0xffff) return 'ipv4';
  return 'ipv6';
}

// Family of an ICE candidate or an RTCStats candidate entry: `address` (standard; Chrome,
// Safari, Firefox), else `ip` (older Chrome/Safari), else `ipAddress` (legacy stats).
export function candidateFamily(candidate) {
  if (!candidate || typeof candidate !== 'object') return null;
  return addressFamily(candidate.address) ?? addressFamily(candidate.ip) ?? addressFamily(candidate.ipAddress);
}

// 'IPv4' | 'IPv6' | null, for display.
export function familyLabel(family) {
  if (family === 'ipv4') return 'IPv4';
  if (family === 'ipv6') return 'IPv6';
  return null;
}

// "UDP · IPv6", "TCP · IPv4", "UDP" (family unknown) or "—".
export function pathLabel(protocol, family) {
  const p = protocol === 'udp' || protocol === 'tcp' ? protocol.toUpperCase() : null;
  const parts = [p, familyLabel(family)].filter(Boolean);
  return parts.length ? parts.join(' · ') : '—';
}
